// Approval grants (Agent Platform 9D). "Human approves, agent executes":
// an approver mints a single-use, 10-minute grant bound to ONE exact action
// (e.g. approving one specific payroll run) and hands it to the agent. The
// scope alone never executes approval-tier actions — the grant is the
// approval, recorded as the human who minted it, under the domain's normal
// segregation-of-duties rules. The server refuses to mint unless the caller
// holds the capability's required permission.
import React, { useCallback, useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { KeySquare, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/features/shared/components/ui/button';
import { listApprovalGrants, mintApprovalGrant, revokeApprovalGrant, type ApprovalGrantRow } from '@/services/developerService';
import { CopyButton, errMsg, inputCls } from './common';

// Grant-mintable capabilities (approval-tier with an agent surface). Grows as
// more approval-tier executions ship; the server validates regardless.
const GRANTABLE = [
  {
    id: 'payroll.run.approve',
    label: 'Approve a payroll run stage',
    resourceLabel: 'Payroll run id (UUID)',
    permissionHint: 'requires the payroll approver permission (Finance / Owner)',
  },
];

export const ApprovalGrantsCard: React.FC = () => {
  const { t } = useTranslation('admin');
  const [capabilityId, setCapabilityId] = useState(GRANTABLE[0].id);
  const [resourceId, setResourceId] = useState('');
  const [minting, setMinting] = useState(false);
  const [grant, setGrant] = useState<string | null>(null);
  const [rows, setRows] = useState<ApprovalGrantRow[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadGrants = useCallback(async () => {
    try {
      setRows(await listApprovalGrants());
    } catch {
      // list is best-effort; the mint flow still works without it
    }
  }, []);
  useEffect(() => { void loadGrants(); }, [loadGrants]);

  const revoke = async (row: ApprovalGrantRow) => {
    setRevoking(row.id);
    try {
      await revokeApprovalGrant(row.id);
      toast.success(t('developer.grant_revoked', { defaultValue: 'Grant revoked — it can no longer be spent.' }));
      await loadGrants();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.grant_revoke_failed', { defaultValue: 'Could not revoke the grant.' }))));
    } finally {
      setRevoking(null);
    }
  };

  const cap = GRANTABLE.find((c) => c.id === capabilityId) || GRANTABLE[0];

  const mint = async () => {
    setMinting(true);
    setGrant(null);
    try {
      const out = await mintApprovalGrant({ capabilityId, resourceId: resourceId.trim() });
      setGrant(out.grant);
      await loadGrants();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('developer.grant_mint_failed', { defaultValue: 'Could not mint the grant.' }))));
    } finally {
      setMinting(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <KeySquare className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">
          {t('developer.grants_title', { defaultValue: 'Approval grants' })}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('developer.grants_desc', {
          defaultValue:
            'Let an agent carry ONE approval you make: mint a single-use grant bound to an exact action and hand it to the agent (e.g. the payroll_run_approve MCP tool or kazi payroll approve-run --grant). It expires in 10 minutes, works exactly once, and the approval is recorded as you — your normal approval permissions and segregation-of-duties rules apply.',
        })}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('developer.grants_action', { defaultValue: 'Action' })}
          </span>
          <select className={inputCls} value={capabilityId} onChange={(e) => setCapabilityId(e.target.value)}>
            {GRANTABLE.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <span className="text-[11px] text-muted-foreground">{cap.permissionHint}</span>
        </label>
        <label className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{cap.resourceLabel}</span>
          <input className={inputCls} value={resourceId} onChange={(e) => setResourceId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000" />
        </label>
      </div>
      <Button size="sm" onClick={mint} disabled={minting || !resourceId.trim()}>
        {minting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
        {t('developer.grants_mint', { defaultValue: 'Mint grant' })}
      </Button>
      {grant && (
        <div className="rounded-md border border-warning bg-warning-soft p-3 space-y-1.5">
          <p className="text-sm font-medium text-warning-strong">
            {t('developer.grants_once', { defaultValue: 'Shown once — single use, expires in 10 minutes.' })}
          </p>
          <div className="flex items-center gap-2">
            <code className="text-[11px] font-mono text-foreground break-all flex-1">{grant}</code>
            <CopyButton text={grant} label={t('developer.grants_copy', { defaultValue: 'Copy grant' })} />
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('developer.grants_recent', { defaultValue: 'Recent grants' })}
          </p>
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
              <code className="text-xs">{r.capabilityId}</code>
              <span className="text-xs text-muted-foreground truncate max-w-[180px]">{r.resourceId}</span>
              <span className={`text-[11px] font-medium rounded-full px-1.5 py-0.5 border ${
                r.status === 'active' ? 'bg-success-soft text-success-strong border-success'
                : r.status === 'used' ? 'bg-muted text-muted-foreground border-border'
                : r.status === 'revoked' ? 'bg-danger-soft text-danger-strong border-danger'
                : 'bg-muted text-muted-foreground border-border'}`}>
                {r.status}
              </span>
              <span className="text-[11px] text-muted-foreground ml-auto">{r.approvedBy}</span>
              {r.status === 'active' && (
                <Button variant="outline" size="sm" disabled={revoking === r.id} onClick={() => void revoke(r)}>
                  {revoking === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('developer.grants_revoke', { defaultValue: 'Revoke' })}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
